import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const SaveToastContext = createContext(null);
const SAVE_TOAST_MS = 3000;

/**
 * App-wide save confirmation toast — one message, always in view.
 * Use for Save / create / record-payment success (not validation errors).
 * Auto-dismisses after 3 seconds.
 */
export function SaveToastProvider({ children }) {
  const [toast, setToast] = useState(null);
  const timerRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const clearSaveToast = useCallback(() => {
    clearTimer();
    setToast(null);
  }, [clearTimer]);

  const flashSave = useCallback(
    (message, options = {}) => {
      const text = String(message || '').trim();
      clearTimer();
      if (!text) {
        setToast(null);
        return;
      }
      setToast({
        message: text,
        aboveMobileSave: Boolean(options.aboveMobileSave),
      });
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        setToast(null);
      }, SAVE_TOAST_MS);
    },
    [clearTimer]
  );

  useEffect(() => () => clearTimer(), [clearTimer]);

  const value = useMemo(
    () => ({ flashSave, clearSaveToast }),
    [flashSave, clearSaveToast]
  );

  return (
    <SaveToastContext.Provider value={value}>
      {children}
      {toast?.message ? (
        <div
          className={
            toast.aboveMobileSave
              ? 'save-toast is-above-mobile-save'
              : 'save-toast'
          }
          role="status"
        >
          {toast.message}
        </div>
      ) : null}
    </SaveToastContext.Provider>
  );
}

export function useSaveToast() {
  const ctx = useContext(SaveToastContext);
  if (!ctx) {
    throw new Error('useSaveToast must be used within SaveToastProvider');
  }
  return ctx;
}
